import {
  flattenConnection,
  Image,
  Link,
  Money,
  useMoney,
} from '@shopify/hydrogen';
import clsx from 'clsx';

import type {
  MoneyV2,
  Product,
  ProductVariant,
  ProductVariantConnection,
} from '@shopify/hydrogen/storefront-api-types';
import {formatNumber, isDiscounted, isNewArrival} from '~/utils';

interface Props {
  product: Product;
  label?: string;
  className?: string;
  loading?: HTMLImageElement['loading'];
  onClick?: () => void;
}

export function ProductCard({
  product,
  label,
  className,
  loading,
  onClick,
}: Props) {
  let cardLabel;

  const cardData = product;
  const productTitleArr = (product?.title ?? 'Product Title').split(' ');

  const {
    image,
    priceV2: price,
    compareAtPriceV2: compareAtPrice,
  } = flattenConnection<ProductVariant>(
    cardData?.variants as ProductVariantConnection,
  )[0] || {};

  if (label) {
    cardLabel = label;
  } else if (isDiscounted(price as MoneyV2, compareAtPrice as MoneyV2)) {
    cardLabel = 'Sale';
  } else if (isNewArrival(product.publishedAt)) {
    cardLabel = 'New';
  }

  const styles = clsx('grid gap-6 relative', className);

  return (
    <Link onClick={onClick} to={`/products/${product.handle}`}>
      <div className={styles}>
        <div className="card-image aspect-[4/5] bg-primary/5">
          <span className="text-notice absolute right-0 top-0 m-4 text-right">
            {cardLabel}
          </span>
          {image && (
            <Image
              className="fadeIn aspect-[4/5] w-full object-cover"
              widths={[320]}
              sizes="320px"
              loaderOptions={{
                crop: 'center',
                scale: 2,
                width: 320,
                height: 400,
              }}
              // @ts-ignore Stock type has `src` as optional
              data={image}
              alt={image.altText || `Picture of ${product.title}`}
              loading={loading}
            />
          )}
        </div>
        <div className="grid gap-1">
          <span className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-left ">
            <div className="text-base text-[#4B2304]">
              {productTitleArr[0]}{' '}
              {productTitleArr[1] == 'Glow' && productTitleArr[1]}
              {productTitleArr.length > 1 && (
                <>
                  {' '}
                  <span className="font-serif italic">
                    {productTitleArr
                      .filter((str, index) => index !== 0 && str !== 'Glow')
                      .join(' ')}
                  </span>
                </>
              )}
            </div>
          </span>
          <div className="flex gap-4">
            <span className="flex gap-4">
              {product?.variants?.nodes?.at(0)?.priceV2 && (
                <div className="text-left">
                  {`From ${formatNumber(
                    product?.variants?.nodes?.at(0)?.priceV2?.amount as string,
                  )} ${product?.variants?.nodes?.at(0)?.priceV2?.currencyCode}`}
                </div>
              )}
              {isDiscounted(price as MoneyV2, compareAtPrice as MoneyV2) && (
                <CompareAtPrice
                  className={'opacity-50'}
                  data={compareAtPrice as MoneyV2}
                />
              )}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function CompareAtPrice({
  data,
  className,
}: {
  data: MoneyV2;
  className?: string;
}) {
  const {currencyNarrowSymbol, withoutTrailingZerosAndCurrency} =
    useMoney(data);

  const styles = clsx('strike', className);

  return (
    <span className={styles}>
      {currencyNarrowSymbol}
      {withoutTrailingZerosAndCurrency}
    </span>
  );
}
